import {
  useState,
  FC,
  HTMLInputTypeAttribute,
  useEffect,
  InputHTMLAttributes,
  useMemo,
} from "react";
import { Pencil } from "lucide-react";

import { SlideOverWrapper } from "../../SlideOverWrapper/SlideOverWrapper";
import {
  NumbericOnlyProps,
  SelectOptionsProps,
} from "../../../../molecules/types";
import { NamedField } from "../NamedField";
import { TextLikeEntryElement } from "../../../../../types";

import {
  SLIDEOVER_EDIT_ICON_ICON_STYLES,
  SLIDEOVER_EDIT_ICON_WRAPPER_STYLES,
} from "../../styles";
import { Acceptable } from "../../../../../../helpers/functions/types";
import { Closeable } from "../../SlideOverWrapper/types";

export type EditFieldSlideOverProps = InputHTMLAttributes<HTMLInputElement> & {
  title: string;
  type?: HTMLInputTypeAttribute;
  slideOverInfo?: string;
  options?: SelectOptionsProps;
  value?: string | number;
} & NumbericOnlyProps;

export const EditFieldSlideOver: FC<
  React.PropsWithChildren<EditFieldSlideOverProps & Acceptable<any> & Closeable>
> = ({
  open,
  value,
  type,
  title,
  onAccept,
  slideOverInfo,
  onClose,
  options,
  onClick: _onClick, // excluded: NamedField renders a NamedRow instead of an input when onClick is present
  ...rest
}) => {
  const [newValue, setNewValue] = useState(value);

  useEffect(() => {
    setNewValue(value);
  }, [value, open]);

  const allowSubmit = useMemo(() => newValue !== value, [newValue, value]);

  const handleInputChange = (target: TextLikeEntryElement) => {
    if (!target) return;
    setNewValue(target.value);
  };

  const handleClick = async () => await onAccept(newValue);

  return (
    <SlideOverWrapper
      open={open}
      title={title}
      buttonProps={{ onClick: handleClick, disabled: !allowSubmit }}
      onClose={onClose}
    >
      {slideOverInfo ? (
        <p className="dark:text-white text-sm">{slideOverInfo}</p>
      ) : (
        <div className={SLIDEOVER_EDIT_ICON_WRAPPER_STYLES}>
          <Pencil className={SLIDEOVER_EDIT_ICON_ICON_STYLES} />
        </div>
      )}
      <NamedField
        {...rest}
        key={title}
        customId={title}
        fieldKey={title}
        value={newValue ?? ""}
        onChange={handleInputChange}
        type={type}
        styleVariant="detailPage"
        options={options}
      />
    </SlideOverWrapper>
  );
};
